import type { MedplumClient } from '@medplum/core';
import type { Address, ContactPoint, Organization } from '@medplum/fhirtypes';
import { PRACTICE_LEGAL_NAME_EXT } from '@unify/fhir-model/constants';
import {
  setOrganizationEin,
  setOrganizationNpi,
  setOrganizationPhone,
} from '@unify/fhir-model/resources/organization';
import type { Practice } from '../pages/users/types/rbac';

/**
 * Persists Practice Settings onto the Organization. Practice owner, practice admin, and
 * Unify admin already hold Organization update, so this is a direct write by the caller.
 * The read resource is patched in place so fields this save does not touch, including the
 * practice-owner extension, stay on the update.
 */
export async function updatePracticeSettings(
  medplum: MedplumClient,
  id: string,
  updates: Partial<Practice>,
): Promise<void> {
  if (!hasOrganizationFields(updates)) return;

  const org = await medplum.readResource('Organization', id);
  let next: Organization = org;

  if (updates.name !== undefined) next = { ...next, name: updates.name };
  if (updates.status !== undefined) next = { ...next, active: updates.status !== 'Inactive' };
  if (updates.email !== undefined) next = setWorkTelecom(next, 'email', updates.email);
  if (updates.phone !== undefined) next = setOrganizationPhone(next, updates.phone);
  if (updates.websiteUrl !== undefined) next = setWorkTelecom(next, 'url', updates.websiteUrl);
  if (updates.legalName !== undefined) next = setLegalName(next, updates.legalName);
  if (updates.taxId !== undefined) next = setOrganizationEin(next, updates.taxId);
  if (updates.npi !== undefined) next = setOrganizationNpi(next, updates.npi);
  if (updates.legalEntityAddress !== undefined) {
    next = setLegalEntityAddress(next, updates.legalEntityAddress);
  }

  await medplum.updateResource(next);
}

function hasOrganizationFields(updates: Partial<Practice>): boolean {
  return (
    updates.name !== undefined ||
    updates.status !== undefined ||
    updates.email !== undefined ||
    updates.phone !== undefined ||
    updates.websiteUrl !== undefined ||
    updates.legalName !== undefined ||
    updates.taxId !== undefined ||
    updates.npi !== undefined ||
    updates.legalEntityAddress !== undefined
  );
}

function setWorkTelecom(
  org: Organization,
  system: 'email' | 'url',
  value: string | undefined,
): Organization {
  const telecom: ContactPoint[] = [...(org.telecom ?? [])].filter((t) => t.system !== system);
  const trimmed = value?.trim();
  if (trimmed) telecom.push({ system, value: trimmed, use: 'work' });
  return { ...org, telecom: telecom.length ? telecom : undefined };
}

function setLegalName(org: Organization, value: string): Organization {
  const extension = (org.extension ?? []).filter((e) => e.url !== PRACTICE_LEGAL_NAME_EXT);
  const trimmed = value.trim();
  if (trimmed) extension.push({ url: PRACTICE_LEGAL_NAME_EXT, valueString: trimmed });
  return { ...org, extension: extension.length ? extension : undefined };
}

function setLegalEntityAddress(
  org: Organization,
  address: NonNullable<Practice['legalEntityAddress']>,
): Organization {
  const line = [address.addressLine1, address.addressLine2]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));
  const city = address.city?.trim();
  const state = address.state?.trim();
  const postalCode = address.zipCode?.trim();
  const rest = (org.address ?? []).slice(1);
  if (!line.length && !city && !state && !postalCode) {
    return { ...org, address: rest.length ? rest : undefined };
  }
  const addr: Address = { use: 'work' };
  if (line.length) addr.line = line;
  if (city) addr.city = city;
  if (state) addr.state = state;
  if (postalCode) addr.postalCode = postalCode;
  return { ...org, address: [addr, ...rest] };
}
